/**
 * The card a station opens. It reports and never transacts: there is no claim
 * button here, and the only control closes the card.
 */

import { useEffect, useRef } from "react";
import type { Station } from "./layout";

interface Props {
  station: Station;
  title: string;
  description: string;
  /** Anchor, in world coordinates already adjusted for scroll. */
  anchor: { left: number; top: number };
  showCoins: boolean;
  closing: boolean;
  onClose(): void;
}

function rewardLine(station: Station, showCoins: boolean): { label: string; earned: boolean } | null {
  if (station.kind === "reward" && station.marker) {
    return { label: station.marker.label, earned: station.marker.state === "earned" };
  }
  const reward = station.quest?.campaign.reward;
  if (!reward) return null;
  if (reward.reward.kind === "credits") {
    if (!showCoins) return null;
    return {
      label: `${reward.reward.value} ${reward.reward.currency ?? "coins"}`,
      // The tag says which side filled the slot, and a grant means it is paid.
      // Status is read first: a reversed grant is a record, not a celebration.
      earned: reward.source === "grant" && reward.status === "fulfilled",
    };
  }
  return {
    label: String(reward.reward.value ?? "Reward"),
    earned: reward.source === "grant" && reward.status === "fulfilled",
  };
}

export function StationCard({ station, title, description, anchor, showCoins, closing, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Focus moves into the card so a keyboard reaches its close control, and
  // escape closes it, which is what a dialog owes anyone not using a mouse.
  useEffect(() => {
    ref.current?.focus();
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const reward = rewardLine(station, showCoins);
  const goal = station.quest?.campaign.goal;
  const completedAt = station.quest?.campaign.completedAt;
  const requirement =
    station.marker?.requirement ?? station.gate?.requirement ?? null;

  const eyebrow =
    station.kind === "gate"
      ? "Gate"
      : station.kind === "reward"
        ? `Season track, tier ${station.marker?.tier}`
        : station.quest?.state === "done"
          ? "Completed"
          : station.quest?.state === "hot"
            ? "In progress"
            : "Ahead on the route";

  return (
    <div
      ref={ref}
      className={`station on${closing ? " closing" : ""}`}
      style={{ left: `${anchor.left}px`, top: `${anchor.top}px` }}
      role="dialog"
      aria-label={title}
      tabIndex={-1}
    >
      <div className="s-eyebrow">{eyebrow}</div>
      <h4>{title}</h4>
      <div className="s-desc">{description}</div>

      {goal && goal.target > 1 && station.quest?.state !== "done" && (
        <>
          <div className="s-bar">
            <div className="bar"><i style={{ width: `${Math.round((goal.current / goal.target) * 100)}%` }} /></div>
          </div>
          <div className="s-count tnum">{goal.current} of {goal.target}</div>
        </>
      )}

      {reward && (
        <div className="s-reward">
          <span className={reward.earned ? "earned" : ""}>{reward.label}</span>
          {reward.earned && completedAt && (
            <span className="when">
              {new Date(completedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </span>
          )}
        </div>
      )}

      {requirement && <div className="s-desc">{requirement}</div>}

      <button type="button" className="s-close" onClick={onClose}>Close</button>
    </div>
  );
}
